<template>
  <div class="bill-detail-page">
    <BillDetailBreadcrumb />

    <!-- 账单基本信息 -->
    <mtd-card style="margin-bottom: 20px">
      <div class="bill-header">
        <div class="bill-title">
          <span class="title-text">{{ billTitle }}</span>
          <mtd-tag v-if="detailObj.statusDesc" size="small" :type="statusTagType">
            正常
          </mtd-tag>
        </div>
      </div>

      <el-descriptions :column="2" size="medium" style="margin-top: 20px">
        <el-descriptions-item label="对账单ID">{{
          detailObj.settlementNo || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="业务线">{{
          detailObj.businessLineDesc || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="合作商类型">{{
          detailObj.partnerModeName || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="合作Region">{{
          detailObj.regionName || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="账期">{{
          formatDate(detailObj.date) || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="出账时间">{{
          detailObj.createTime || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="账单状态">{{
          detailObj.statusDesc || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="账单确认人">{{
          detailObj.confirmMisId || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="账单确认时间">{{
          detailObj.confirmTime || '-'
        }}</el-descriptions-item>
      </el-descriptions>
    </mtd-card>

    <!-- 账单明细 -->
    <mtd-card>
      <div class="settlement-amount-section">
        <div class="settlement-amount-label">结算金额</div>
        <div class="settlement-amount-value">
          {{ formatAmount(detailObj.settlementAmount) }}
        </div>
        <div class="settlement-amount-unit">元</div>

        <div style="flex: 1; display: flex; align-items: center; justify-content: flex-end;">
          <mtd-button
            v-if="showConfirmButton"
            type="primary"
            @click="handleConfirmBill"
            v-auth="'/api/partner/outside/platform/agent/settlementOrder/supplier/confirm'"
          >
            确认账单
          </mtd-button>
        </div>
      </div>

      <div class="bill-detail-grid">
        <div v-for="item in billDetailItems" :key="item.key" class="detail-item">
          <div class="detail-label">
            <span>{{ item.label }}</span>
            <mtd-tooltip v-if="item.tooltip" :content="item.tooltip" theme="light" placement="top">
              <i class="mtdicon mtdicon-question-circle-o"></i>
            </mtd-tooltip>
          </div>
          <div class="detail-value" :class="{ negative: item.isNegative }">
            <span v-if="item.isPercentage">
              {{ formatPercentage(detailObj[item.key] || getDetailValue(item.key)) }}
            </span>
            <span v-else>
              {{ formatAmount(detailObj[item.key] || getDetailValue(item.key), item.showSign) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 计算公式提示 -->
      <div class="formula-section">
        <div class="formula-item">
          <span class="formula-label">结算金额 = </span>
          <span class="formula-text">应付商家小计 - 抵扣金额小计</span>
        </div>
        <div class="formula-item">
          <span class="formula-label">应付商家小计 = </span>
          <span class="formula-text"
            >应付商家款 + 实际调整金额 -
            实收流水历史欠款。应付商家款=每一笔流水扣除服务费所有流水加和</span
          >
        </div>
      </div>

      <!-- Tab区域 -->
      <mtd-tabs v-model="activeTab" style="margin-top: 24px">
        <!-- 应付商家小计 -->
        <mtd-tab-pane :label="payToPartnerTotalLabel" value="revenue">
          <div class="tab-content">
            <div
              v-for="(item, index) in payToPartnerTotalList"
              :key="index"
              class="pay-to-partner-item"
            >
              <!-- 主项 -->
              <div class="detail-row main-item">
                <div class="detail-label main-label">{{ item.label }}</div>
                <div class="detail-value" :class="{ negative: isNegativeValue(item.value) }">
                  <span v-if="isPercentageLabel(item.label)">
                    {{ formatPercentage(item.value) }}
                  </span>
                  <span v-else> {{ formatAmount(item.value, false) }} 元 </span>
                </div>
              </div>

              <!-- 子项（如果有detail） -->
              <!-- 调整金额使用adjustmentDetail显示（包含备注信息），其他使用detail -->
              <div
                v-if="item.label === '调整金额' && adjustmentDetail.length > 0"
                class="sub-items"
              >
                <div
                  v-for="(adjItem, adjIndex) in adjustmentDetail"
                  :key="adjIndex"
                  class="detail-row sub-item"
                >
                  <div class="detail-label sub-label">{{ adjItem.label }}</div>
                  <div class="detail-value">
                    {{ formatAmount(adjItem.amount, false) }} 元
                    <span v-if="adjItem.remark" class="detail-remark"
                      >（{{ adjItem.remark }}）</span
                    >
                  </div>
                </div>
              </div>
              <div v-else-if="item.detail && item.detail.length > 0" class="sub-items">
                <div
                  v-for="(subItem, subIndex) in item.detail"
                  :key="subIndex"
                  class="detail-row sub-item"
                >
                  <div class="detail-label sub-label">{{ subItem.label }}</div>
                  <div class="detail-value">
                    <span v-if="isPercentageLabel(subItem.label)">
                      {{ formatPercentage(subItem.value) }}
                    </span>
                    <span v-else> {{ formatAmount(subItem.value, false) }} 元 </span>
                    <span v-if="subItem.remark" class="detail-remark"
                      >（{{ subItem.remark }}）</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </mtd-tab-pane>

        <!-- 抵扣金额小计 -->
        <mtd-tab-pane :label="deductionTotalLabel" value="deduction">
          <div class="tab-content">
            <div v-for="(item, index) in deductionList" :key="index" class="deduction-item">
              <div class="detail-row">
                <div class="detail-label">{{ item.feeName }}</div>
                <div class="detail-value">
                  {{ formatAmount(item.currentDeduction, false) }} 元
                  <span v-if="item.remark" class="detail-remark">（{{ item.remark }}）</span>
                </div>
              </div>
            </div>
            <!-- 小计行 -->
            <div class="deduction-item">
              <div class="detail-row main-item">
                <div class="detail-label main-label">抵扣金额小计</div>
                <div class="detail-value">
                  {{ formatAmount(detailObj.deductionTotal || 0, false) }} 元
                </div>
              </div>
            </div>
          </div>
        </mtd-tab-pane>
      </mtd-tabs>
    </mtd-card>

    <!-- 确认账单弹窗 -->
    <mtd-modal title="确认账单" :visible.sync="confirmDialogVisible" :closable="false">
      <div class="confirm-modal-content">
        是否已经对单据信息确认完毕，后续会依据该单据信息生成账单进行结算，请仔细确认并保证确认无误。
      </div>
      <div slot="footer">
        <mtd-button @click="confirmDialogVisible = false">取消</mtd-button>
        <mtd-button type="primary" @click="doConfirmBill" :loading="confirmLoading">
          确定
        </mtd-button>
      </div>
    </mtd-modal>
  </div>
</template>

<script>
import BillDetailBreadcrumb from './components/BillDetailBreadcrumb.vue'
// import { api } from '@/api/const'
import { format } from 'date-fns'

export default {
  name: 'BillDetail',
  components: {
    BillDetailBreadcrumb,
  },
  data() {
    return {
      detailObj: {},
      detailFeeData: [],
      activeTab: 'revenue',
      confirmDialogVisible: false,
      confirmLoading: false,
      loading: false,
      settlementNo: '',
      billDetailItems: [
        {
          key: 'receivables',
          label: '实收流水',
          tooltip:
            '统计周期内，用户交易完成的实际订单收入(包含：骑行交易、罚款)及卡券摊销收入，且需减去用户实际退款。电单车实收流水=电单车骑行实收+电单车罚款实收-电单车实收退款+摊销',
          showSign: false,
          isNegative: false,
        },
        {
          key: 'payablePartnerTotal',
          label: '应付商家小计',
          tooltip: '实收流水中，包含调整金额之后，扣除服务费、实收流水历史欠款之后应付的金额。',
          showSign: false,
          isNegative: false,
        },
        {
          key: 'serviceFeeTotal',
          label: '服务费小计',
          tooltip:
            '每一笔用户支付的订单以及卡券摊销流水*服务费率，计算结果四舍五入后，所有服务费的金额总和，退款不退服务费',
          showSign: false,
          isNegative: false,
        },
        // {
        //   key: 'serviceFeeRate',
        //   label: '服务费率',
        //   tooltip: '当期账单对应的服务费率',
        //   showSign: false,
        //   isNegative: false,
        //   isPercentage: true,
        // },
        {
          key: 'adjustmentTotal',
          label: '调整金额小计',
          tooltip: '当期账单的实际抵扣费用项总额',
          showSign: false,
          isNegative: false,
        },
        {
          key: 'deductionTotal',
          label: '抵扣金额小计',
          tooltip: '当期账单的实际抵扣费用项总额',
          showSign: false,
          isNegative: false,
        },
        {
          key: 'receivablesHistoryDebt',
          label: '实收流水历史欠款',
          tooltip: '如果账期当天流水没有支付只有退款，会记录为到实收流水欠款',
          showSign: false,
          isNegative: false,
        },
      ],
      revenueItems: [
        { key: 'rideReceivables', label: '骑行实收总金额', showSign: false, isNegative: false },
        { key: 'fineTotal', label: '罚款总金额', showSign: false, isNegative: false },
        { key: 'refundTotal', label: '退款总金额', showSign: false, isNegative: false },
        { key: 'couponAmortization', label: '卡券摊销金额', showSign: false, isNegative: false },
        {
          key: 'serviceFeeRate',
          label: '服务费率',
          showSign: false,
          isNegative: false,
          isPercentage: true,
        },
        { key: 'serviceFeeTotal', label: '服务费小计', showSign: false, isNegative: false },
        { key: 'adjustmentTotal', label: '调整金额小计', showSign: false, isNegative: false },
        {
          key: 'receivablesHistoryDebt',
          label: '实收流水历史欠款',
          showSign: false,
          isNegative: false,
        },
        { key: 'payablePartnerTotal', label: '应付商家小计', showSign: false, isNegative: false },
      ],
      adjustmentDetail: [],
      deductionList: [],
      payToPartnerTotalList: [], // 应付商家小计相关明细数据
    }
  },
  computed: {
    billTitle() {
      const partnerName = this.detailObj.partnerName || ''
      const date = this.formatDate(this.detailObj.date) || ''
      return `${partnerName}${date}账单`
    },
    showConfirmButton() {
      // 账单状态为待确认（已出账）时显示确认按钮
      return this.detailObj.status === 10 || this.detailObj.statusDesc === '待确认'
    },
    statusTagType() {
      if (this.detailObj.statusDesc === '已确认') {
        return 'success'
      }
      return 'warning'
    },
    payToPartnerTotalLabel() {
      const amount = this.formatAmount(this.detailObj.payablePartnerTotal || 0)
      return `应付商家小计 ${amount} 元`
    },
    deductionTotalLabel() {
      const amount = this.formatAmount(this.detailObj.deductionTotal || 0)
      return `抵扣金额小计 ${amount} 元`
    },
  },
  mounted() {
    this.init()
    this.getBillDetail()
  },
  methods: {
    init() {
      this.settlementNo = this.$route.query.settlementNo || this.$route.query.id
      if (!this.settlementNo) {
        this.$message.error('缺少账单ID参数')
        this.$router.back()
      }
    },
    async getBillDetail() {
      this.loading = true
      try {
        const params = {
          settlementOrderNo: this.settlementNo,
        }

        // 获取账单详情数据
        const res = await this.fetch.get('getOrderDetailFee', { params })
        if (res?.data) {
          const { settleOrder, settleOrderDetail, payToPartnerTotal, deductAmountDetail } = res.data

          // 处理账单基本信息 (settleOrder)
          if (settleOrder && Array.isArray(settleOrder)) {
            settleOrder.forEach(item => {
              const key = this.getBasicInfoKeyByLabel(item.label)
              if (key) {
                this.$set(this.detailObj, key, item.value)
              }
            })
          }

          // 处理账单明细 (settleOrderDetail)
          if (settleOrderDetail && Array.isArray(settleOrderDetail)) {
            settleOrderDetail.forEach(item => {
              const key = this.getDetailKeyByLabel(item.label)
              if (key) {
                // 处理服务费率，如果是 "-" 则设为空
                this.$set(this.detailObj, key, item.value)
              }
            })
          }

          // 处理应付商家小计相关明细 (payToPartnerTotal)
          if (payToPartnerTotal && Array.isArray(payToPartnerTotal)) {
            // 保存原始数据用于展示
            this.payToPartnerTotalList = payToPartnerTotal.map(item => ({
              label: item.label,
              value: item.value,
              detail: item.detail || [],
            }))

            payToPartnerTotal.forEach(item => {
              const key = this.getDetailKeyByLabel(item.label)
              if (key) {
                this.$set(this.detailObj, key, item.value)
              }

              // 处理实收流水的明细（骑行实收总金额、卡券摊销金额、罚款、退款）
              if (item.label === '实收流水' && item.detail && Array.isArray(item.detail)) {
                item.detail.forEach(detailItem => {
                  const detailKey = this.getDetailKeyByLabel(detailItem.label)
                  if (detailKey) {
                    this.$set(this.detailObj, detailKey, detailItem.value)
                  }
                })
              }

              // 处理调整金额明细（长期激励、专项激励）
              if (item.label === '调整金额' && item.detail && Array.isArray(item.detail)) {
                this.adjustmentDetail = item.detail.map(detailItem => ({
                  label: detailItem.label || '',
                  amount: detailItem.value === '-' ? 0 : Number(detailItem.value) || 0,
                  remark: detailItem.remark || '',
                }))
              }
            })
          }

          // 处理抵扣金额明细 (deductAmountDetail)
          if (deductAmountDetail && Array.isArray(deductAmountDetail)) {
            this.deductionList = deductAmountDetail.map(item => ({
              feeName: item.label || '',
              historyDebt: 0, // 新接口没有历史欠款字段
              currentDeduction: item.value === '-' ? 0 : Number(item.value) || 0,
              remark: item.remark || '',
            }))
          }
        }
      } catch (error) {
        console.error('获取账单详情失败:', error)
        // this.$message.error('获取账单详情失败')
      } finally {
        this.loading = false
      }
    },
    getBasicInfoKeyByLabel(label) {
      const labelMap = {
        合作商名称: 'partnerName',
        对账单ID: 'settlementNo',
        业务线: 'businessLineDesc',
        合作商类型: 'partnerModeName',
        合作Region: 'regionName',
        账期: 'date',
        出账时间: 'createTime',
        账单状态: 'statusDesc',
        账单确认人: 'confirmMisId',
        账单确认时间: 'confirmTime',
      }
      return labelMap[label]
    },

    // 补充接口数据key
    getDetailKeyByLabel(label) {
      const labelMap = {
        实收流水: 'receivables',
        应付商家小计: 'payablePartnerTotal',
        服务费小计: 'serviceFeeTotal',
        服务费率: 'serviceFeeRate',
        调整金额小计: 'adjustmentTotal',
        调整金额: 'adjustmentTotal',
        抵扣金额小计: 'deductionTotal',
        抵扣金额: 'deductionTotal',
        抵扣费用小计: 'deductionTotal',
        实收流水历史欠款: 'receivablesHistoryDebt',
        结算金额: 'settlementAmount',
        骑行实收总金额: 'rideReceivables',
        罚款: 'fineTotal',
        罚款总金额: 'fineTotal',
        退款: 'refundTotal',
        退款总金额: 'refundTotal',
        卡券摊销金额: 'couponAmortization',
      }
      return labelMap[label]
    },
    getDetailValue(key) {
      // 从 detailFeeData 中获取值
      const item = this.detailFeeData.find(item => {
        const mappedKey = this.getDetailKeyByLabel(item.label)
        return mappedKey === key
      })
      return item ? item.value : ''
    },
    formatAmount(amount, showSign = false) {
      if (amount === null || amount === undefined || amount === '' || amount === '-') {
        return '-'
      }
      const number = Number(amount)
      if (isNaN(number)) return '-'
      // 分->元
      //   number = number / 100

      const formatted = number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

      if (showSign) {
        if (number > 0) {
          return '+' + formatted
        } else if (number < 0) {
          return formatted
        } else {
          return '0.00'
        }
      }

      return formatted
    },
    formatPercentage(value) {
      if (value === null || value === undefined || value === '' || value === '-') {
        return '-'
      }
      const number = Number(value)
      if (isNaN(number)) return '-'
      return (number * 100).toFixed(2) + '%'
    },
    formatDate(date) {
      if (!date) return ''
      // 如果已经是格式化的日期字符串，直接返回
      if (typeof date === 'string' && date.includes('-')) {
        return date
      }
      // 如果是时间戳，格式化
      if (typeof date === 'number') {
        return format(new Date(date), 'yyyy-MM-dd')
      }
      return date
    },
    isPercentageLabel(label) {
      return label && label.includes('费率')
    },
    isNegativeValue(value) {
      if (value === '-' || value === null || value === undefined) return false
      const num = Number(value)
      return !isNaN(num) && num < 0
    },
    handleConfirmBill() {
      this.confirmDialogVisible = true
    },
    async doConfirmBill() {
      this.confirmLoading = true
      try {
        // 调用确认账单接口
        const params = {
          settlementOrderNo: this.settlementNo,
        }
        await this.fetch.get('saveBillConfirm', { params })
        this.$message.success('确认成功')
        this.confirmDialogVisible = false
        // 刷新详情数据
        await this.getBillDetail()
      } catch (error) {
        console.error('确认账单失败:', error)
        this.$message.error(error.message || '确认账单失败')
      } finally {
        this.confirmLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bill-detail-page {
  padding: 20px;
}

.bill-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.bill-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-text {
  font-size: 20px;
  font-weight: 600;
  color: rgba(17, 25, 37, 0.85);
}

.settlement-amount-section {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 16px;
  background-color: #fff5f0;
  border-radius: 6px;
  margin-bottom: 24px;
}

.settlement-amount-label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(17, 25, 37, 0.85);
}

.settlement-amount-value {
  font-size: 24px;
  font-weight: 600;
  color: #f56200;
  font-family: DIN;
}

.settlement-amount-unit {
  font-size: 14px;
  color: rgba(17, 25, 37, 0.85);
}

.bill-detail-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 24px;
  padding-left: 12px;
}

.detail-item {
  .detail-label {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;
    font-size: 14px;
    color: rgba(17, 25, 37, 0.65);

    .mtdicon {
      color: rgba(17, 25, 37, 0.45);
      cursor: help;
    }
  }

  .detail-value {
    font-size: 16px;
    font-weight: 500;
    color: rgba(17, 25, 37, 0.85);

    &.negative {
      color: #ff4d4f;
    }
  }
}

.formula-section {
  padding: 16px;
  background-color: #f0f7ff;
  border-radius: 6px;
  margin-bottom: 24px;
}

.formula-item {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.formula-label {
  font-weight: 600;
  color: rgba(17, 25, 37, 0.85);
}

.formula-text {
  color: rgba(17, 25, 37, 0.65);
}

.tab-content {
  padding: 20px 0;
}

.pay-to-partner-item {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.deduction-item {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  // &.main-item {
  //   border-bottom: 1px solid #e0e0e0;
  // }

  &.sub-item {
    border-bottom: 1px solid #f5f5f5;
    padding-left: 24px;
  }

  &:last-child {
    border-bottom: none;
  }

  .detail-label {
    font-size: 14px;
    color: rgba(17, 25, 37, 0.65);
    flex: 0 0 auto;
    min-width: 140px;

    &.main-label {
      font-weight: 600;
      color: rgba(17, 25, 37, 0.85);
    }

    &.sub-label {
      font-weight: 400;
      color: rgba(17, 25, 37, 0.65);
    }
  }

  .detail-value {
    font-size: 14px;
    font-weight: 500;
    color: rgba(17, 25, 37, 0.85);
    flex: 1;
    margin-left: 24px;

    &.negative {
      color: #ff4d4f;
    }
  }

  .detail-remark {
    font-size: 12px;
    color: rgba(17, 25, 37, 0.45);
    margin-left: 8px;
    font-weight: 400;
  }
}

.sub-items {
  padding-left: 0;
}

.adjustment-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(17, 25, 37, 0.85);
  margin-bottom: 16px;
}

.adjustment-list {
  .adjustment-item {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    .adjustment-label {
      font-size: 14px;
      color: rgba(17, 25, 37, 0.65);
      width: 120px;
      flex-shrink: 0;
    }

    .adjustment-value-wrapper {
      flex: 1;
    }

    .adjustment-value {
      font-size: 14px;
      font-weight: 500;
      color: rgba(17, 25, 37, 0.85);
      margin-bottom: 4px;

      &.negative {
        color: #ff4d4f;
      }
    }

    .adjustment-remark {
      font-size: 12px;
      color: rgba(17, 25, 37, 0.45);
    }
  }
}

.confirm-modal-content {
  padding: 20px 0;
  line-height: 1.6;
  color: rgba(17, 25, 37, 0.85);
}
</style>
